import React, { useCallback, useMemo } from 'react';
import { Box } from '@wds/box';
import { IconUser } from '@wds/icons';
import { Typography } from '@wds/typography';
import { SelectMenu } from '@wds/select-menu';

import { SelectOptions } from './types';
import { usePricingState } from '~shared/hooks/usePricingState';
import useStyles from './useStyles';
import { Packages } from '~shared/constants/pricing';
import ACTIONS from '~shared/actions';
import { CombinedActions } from '~shared/types/pricing/actions';

import { determinePackageSwap } from '../../helpers';

export default function UserSelector({ packageName }: Readonly<{ packageName: Packages }>): JSX.Element {
  const { SelectUser } = useStyles();

  const {
    state: {
      grow2792: { advantageUsers, premierUsers },
    },
    dispatch,
  } = usePricingState();

  const isAdvantagePackage = [Packages.AdvantageAnnual, Packages.TeamAdvantage].includes(packageName);
  const isPremierPackage = [Packages.PremierAnnual, Packages.TeamPremier].includes(packageName);
  const isEnterprise = packageName === Packages.Enterprise;

  const getUserText = (num: string | number): string => {
    return Number(num) > 1 ? 'users' : 'user';
  };

  const selectedUserInfo = useMemo(() => {
    if (isAdvantagePackage) {
      return {
        text: getUserText(advantageUsers),
        count: advantageUsers,
      };
    }

    if (isPremierPackage) {
      return {
        text: getUserText(premierUsers),
        count: premierUsers,
      };
    }

    if (isEnterprise) {
      return { text: `5+ ${getUserText(5)}` };
    }

    return { text: `1 ${getUserText(1)}` };
  }, [advantageUsers, isAdvantagePackage, isEnterprise, isPremierPackage, premierUsers]);

  const MAX_OPTIONS: SelectOptions[] = Array(23)
    .fill(3)
    .map((start: number, end: number): SelectOptions => {
      const opt = (start + end).toString();
      return {
        value: opt,
        text: opt,
      };
    });

  const OPTIONS: SelectOptions[] = [
    {
      value: '',
      text: 'Individual user',
    },
    {
      value: '1',
      text: '1',
    },
    {
      value: '',
      text: 'Multiple users (3 user minimum)',
    },
    ...MAX_OPTIONS,
  ];

  const handleUserChange = useCallback(
    (e): void => {
      const numberOfUsers = e.target.innerText;

      if (
        (isAdvantagePackage && numberOfUsers !== advantageUsers) ||
        (isPremierPackage && numberOfUsers !== premierUsers)
      ) {
        determinePackageSwap(numberOfUsers, isAdvantagePackage, isPremierPackage, dispatch);

        dispatch({
          type: ACTIONS.PNP_REFRESH_CHANGE_USERS,
          payload: { users: numberOfUsers, packageType: isAdvantagePackage ? 'advantageUsers' : 'premierUsers' },
        } as CombinedActions);
      }
    },

    [advantageUsers, dispatch, isAdvantagePackage, isPremierPackage, premierUsers]
  );

  return (
    <Box className={SelectUser}>
      <IconUser title="user" size="sm" />
      {(isAdvantagePackage || isPremierPackage) && (
        <SelectMenu value={selectedUserInfo.count?.toString()} size="sm" onChange={handleUserChange}>
          {OPTIONS.map(({ value, text }) => (
            <SelectMenu.Option key={`${packageName}-userSelection-${text}`} value={value} disabled={!value.length}>
              {text}
            </SelectMenu.Option>
          ))}
        </SelectMenu>
      )}
      <Typography component="p" align="right">
        {selectedUserInfo.text}
      </Typography>
    </Box>
  );
}
